import { format } from 'date-fns'

/**
 * The ONE way to turn a Date into a calendar date string (YYYY-MM-DD).
 *
 * NEVER use `toISOString().slice(0,10)` for this. `toISOString()` converts to UTC
 * first, so a Date at LOCAL midnight in Zürich (UTC+1/+2) serialises to the
 * PREVIOUS day — which silently shifted every task-calendar cell, every "this
 * month" finance range, and every overdue comparison by one day for every user in
 * the studio's own timezone. See CalendarPage.tsx dayCellClassNames for the
 * hand-rolled version of this fix that shipped before the helper existed.
 *
 * date-fns `format` reads the LOCAL calendar fields, which is what a user-visible
 * calendar date means. Same function the DatePicker already uses.
 */
export function formatLocalDate(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}

/** Today's calendar date in the user's timezone, as YYYY-MM-DD. */
export function todayLocalDate(): string {
  return formatLocalDate(new Date())
}

/** `todayLocalDate()` shifted by whole calendar days (DST-safe: uses setDate). */
export function localDatePlusDays(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return formatLocalDate(d)
}

/**
 * Parse a `YYYY-MM-DD` calendar date into a Date at LOCAL midnight.
 * `new Date("2026-07-29")` parses as UTC midnight per spec (= 02:00 CEST), which
 * makes "is this due today?" comparisons wrong from 02:00 onwards every day.
 */
export function parseLocalDate(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number)
  return new Date(y, m - 1, d)
}
